import { Fragment, useCallback, useMemo, useState } from 'react'

import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { createColumnHelper, type Table } from '@tanstack/react-table'
import { z } from 'zod'

import { CheckCircleMiniSolid } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Drawer } from '@/components/ui/drawer'
import { InputSearch } from '@/components/ui/input/input-search'
import { PageCard, PageLayout } from '@/components/ui/page'
import {
	CellContent,
	CellText,
	CellTextDate,
	CellTextHeader,
	CellTextIdr,
	useDataTable,
} from '@/components/ui/table/data-table'
import { DataTable } from '@/components/ui/table/data-table/data-table'
import { DataTableColumnHeader } from '@/components/ui/table/data-table/data-table-column-header'
import { Text } from '@/components/ui/text'
import { toast } from '@/components/ui/toaster/util'

import { invalidateQueries } from '@/libs/helper'
import { useAuth } from '@/libs/hooks/use-auth'

import {
	itemMasterAssignToOutlet,
	itemMasterDetail,
	itemMasterList,
	itemOutletList,
} from '@/features/item/api'
import { ItemMasterTypeBadge } from '@/features/item/components/common'
import type { IItemMaster } from '@/features/item/type'

const searchParamsSchema = z.object({
	q: z.string().optional(),
})

export const Route = createFileRoute('/_app/inventory/master/')({
	validateSearch: searchParamsSchema,
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/inventory', 'Inventory'],
					[Route.fullPath, 'Master Bahan'],
				]}
			/>
			<PageCard.Root>
				<PageCard.Header
					title="Daftar Master Item"
					actions={
						<PageCard.HeaderAction asChild>
							<Link from={Route.fullPath} to="/inventory/master/create">
								Tambah bahan baru
							</Link>
						</PageCard.HeaderAction>
					}
				/>
				<ItemMasterTable />
			</PageCard.Root>
		</PageLayout.Root>
	)
}

function ItemMasterTable() {
	const navigate = Route.useNavigate()
	const { q } = Route.useSearch()
	const { outletId, outletName } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(itemMasterList.qry({ outletId }))
	const columns = useMemo(getColumns, [outletId])
	const { table } = useDataTable({
		state: { globalFilter: q },
		columns,
		data: data?.items ?? [],
		getRowId: (row) => row.id,
	})

	return (
		<Fragment>
			<PageCard.ContentSection className="flex justify-between py-2">
				<InputSearch
					defaultValue={q}
					onChange={(s) => navigate({ search: { q: s } })}
					placeholder="Cari item..."
				/>
				<DrawerAddToOutlet
					outletId={outletId}
					outletName={outletName}
					table={table}
					isLoading={isLoading}
				/>
			</PageCard.ContentSection>
			<DataTable
				table={table}
				columns={columns}
				isLoading={isLoading}
				onRowClick={(row) =>
					navigate({
						to: '/inventory/master/$id',
						params: { id: row.original.id },
					})
				}
			/>
		</Fragment>
	)
}

interface DrawerAddToOutletProps {
	outletId: string
	outletName: string
	table: Table<IItemMaster>
	isLoading: boolean
}

function DrawerAddToOutlet({
	outletId,
	outletName,
	table,
	isLoading,
}: DrawerAddToOutletProps) {
	const [open, setOpen] = useState(false)
	const { mutateAsync, isPending } = useMutation({
		mutationFn: itemMasterAssignToOutlet.api,
		onSuccess: () => {
			invalidateQueries([
				itemMasterList.key,
				itemMasterDetail.key,
				itemOutletList.key,
			])
			setOpen(false)
			table.resetRowSelection()
		},
	})

	const items = isLoading ? [] : table.getSelectedRowModel().rows

	const onClickAddToOutlet = useCallback(() => {
		const itemIds = table.getSelectedRowModel().rows.map((i) => i.original.id)
		if (!itemIds || !itemIds.length) {
			toast.error('Silahkan pilih data!')
			return
		}
		toast.promise(mutateAsync({body: { outletId, imIds: itemIds }}), {
			loading: 'Menambahkan...',
			success: 'Berhasil',
			error: 'Gagal',
		})
	}, [mutateAsync, outletId, table])

	return (
		<Drawer open={open} onOpenChange={setOpen}>
			<Drawer.Trigger asChild>
				<Button variant="secondary" size="small" disabled={!items.length}>
					Tambah ke Outlet
				</Button>
			</Drawer.Trigger>
			<Drawer.Content>
				<Drawer.Header>
					<Drawer.Title>
						Tambah ke Outlet&nbsp;
						<strong>"{outletName}"</strong>
					</Drawer.Title>
				</Drawer.Header>
				<Drawer.Body className="overflow-auto">
					<div
						className="grid divide-y"
						style={{ gridTemplateColumns: '3fr 1fr' }}
					>
						{items.length > 0 &&
							items.map(({ original }) => {
								const { name, type, id } = original
								return (
									<div
										key={id}
										className="col-span-full grid grid-cols-subgrid py-2"
									>
										<Text>{name}</Text>
										<ItemMasterTypeBadge type={type} />
									</div>
								)
							})}
					</div>
				</Drawer.Body>
				<Drawer.Footer>
					<Drawer.Close asChild>
						<Button variant="secondary">Cancel</Button>
					</Drawer.Close>
					<Button isLoading={isPending} onClick={onClickAddToOutlet}>
						Save
					</Button>
				</Drawer.Footer>
			</Drawer.Content>
		</Drawer>
	)
}

const ch = createColumnHelper<IItemMaster>()

function getColumns() {
	return [
		ch.display({
			id: 'select',
			header: ({ table }) => {
				return (
					<Checkbox
						checked={
							table.getIsSomePageRowsSelected()
								? 'indeterminate'
								: table.getIsAllPageRowsSelected()
						}
						onCheckedChange={(value) =>
							table.toggleAllPageRowsSelected(!!value)
						}
					/>
				)
			},
			cell: ({ row }) => {
				return (
					<Checkbox
						checked={row.getIsSelected()}
						onCheckedChange={(v) => row.toggleSelected(!!v)}
						onClick={(e) => e.stopPropagation()}
					/>
				)
			},
		}),
		ch.accessor('name', {
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Nama" />
			),
			cell: ({ row }) => <CellText text={row.original.name} />,
		}),
		ch.accessor('type', {
			header: () => <CellTextHeader text="Jenis" align="center" />,
			cell: ({ row }) => (
				<CellContent className="flex items-center justify-center">
					<ItemMasterTypeBadge type={row.original.type} />
				</CellContent>
			),
		}),
		ch.accessor('baseCost', {
			header: () => <CellTextHeader text="Harga" />,
			cell: ({ row }) => <CellTextIdr idr={row.original.baseCost} />,
		}),
		ch.accessor('inOutlet', {
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="In Outlet" />
			),
			cell: ({ row }) => {
				return (
					<CellContent className="flex items-center justify-center">
						{row.original.inOutlet ? <CheckCircleMiniSolid /> : <CellText />}
					</CellContent>
				)
			},
		}),
		ch.accessor('alert.enabled', {
			header: 'Stock Alert',
			cell: ({ row }) => {
				const { enabled, min } = row.original.alert || {}
				return <CellText text={enabled ? `${min} atau lebih` : 'Tidak aktif'} />
			},
		}),
		ch.accessor('createdAt', {
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Created At" />
			),
			cell: ({ row }) => {
				return <CellTextDate date={row.original.createdAt} />
			},
		}),
		ch.accessor('updatedAt', {
			header: ({ column }) => (
				<DataTableColumnHeader column={column} title="Updated At" />
			),
			cell: ({ row }) => {
				return <CellTextDate date={row.original.updatedAt} />
			},
		}),
	]
}
